import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { TeacherSidebar } from '../TeacherSidebar';
import { Footer } from '../Footer';
import { MenuIcon, XIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import toast from 'react-hot-toast';
interface TeacherLayoutProps {
  children: React.ReactNode;
}
export const TeacherLayout: React.FC<TeacherLayoutProps> = ({
  children
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const location = useLocation();
  const isExpanded = isPinned || isHovered;
  // Detect messages page to remove padding for edge-to-edge messaging layout
  const isMessagesPage = location.pathname === '/teacher-messages';
  // Real-time alert listener for toast notifications
  const isInitialLoadRef = useRef(true);
  useEffect(() => {
    if (!currentUser?.uid) return;
    isInitialLoadRef.current = true;
    const alertsRef = collection(db, 'teachers', currentUser.uid, 'alerts');
    const alertsQuery = query(alertsRef, orderBy('createdAt', 'desc'), limit(20));
    const unsubscribe = onSnapshot(alertsQuery, snapshot => {
      if (isInitialLoadRef.current) {
        // Skip initial load — don't toast for existing alerts
        isInitialLoadRef.current = false;
        return;
      }
      // Only show toasts for newly added documents
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added') {
          const data = change.doc.data();
          const message = data.message || 'New notification';
          toast(message, {
            duration: 4000,
            position: 'top-right',
            icon: '🔔',
            style: {
              borderRadius: '12px',
              background: isDark ? '#1e2a4a' : '#fff',
              color: isDark ? '#e5e7eb' : '#1f2937',
              border: isDark ? '1px solid #334155' : '1px solid #e5e7eb',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '14px',
              padding: '12px 16px'
            }
          });
        }
      });
    }, error => {
      console.warn('[TeacherLayout] Alert listener error:', error);
    });
    return () => unsubscribe();
  }, [currentUser?.uid, isDark]);
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const handlePinToggle = () => {
    setIsPinned(prev => !prev);
  };
  const handleArrowToggle = () => {
    if (isPinned) {
      // Unpin and collapse
      setIsPinned(false);
      setIsHovered(false);
    } else {
      // Pin open
      setIsPinned(true);
    }
  };
  const handleMouseEnter = () => {
    if (!isPinned) {
      setIsHovered(true);
    }
  };
  const handleMouseLeave = () => {
    if (!isPinned) {
      setIsHovered(false);
    }
  };
  return <div className={`min-h-dvh ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-1659">
      <Navbar setIsDeleteAccountModalOpen={() => {}} data-id="element-1660" />
      {/* Mobile sidebar toggle button — hidden on Messages page to avoid overlapping Send button */}
      {!isMessagesPage && <div className="lg:hidden fixed bottom-6 right-6 z-50" data-id="element-1661">
          <button onClick={toggleSidebar} className="bg-deep-pink hover:bg-burgundy text-white p-3 rounded-full shadow-lg transition-colors" aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'} data-id="element-1662">
            {sidebarOpen ? <XIcon className="h-6 w-6" data-id="element-1663" /> : <MenuIcon className="h-6 w-6" data-id="element-1664" />}
          </button>
        </div>}
      <div className="flex" data-id="element-1665">
        {/* Desktop sidebar - hover expand + pin */}
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
        width: isExpanded ? 256 : 72
      }} className={`hidden lg:flex flex-col fixed h-full overflow-y-auto overflow-x-hidden pt-20 ${isDark ? 'bg-navy-800 shadow-lg' : 'bg-white shadow-md'} z-20 transition-all duration-200 ease-in-out`} data-id="element-1666">
          <TeacherSidebar isExpanded={isExpanded} isPinned={isPinned} onPinToggle={handlePinToggle} onArrowToggle={handleArrowToggle} data-id="element-1667" />
        </div>
        {/* Mobile sidebar - overlay */}
        {sidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} data-id="element-1668"></div>}
        {/* Mobile sidebar - content */}
        <div className={`fixed inset-y-0 left-0 z-20 w-64 ${isDark ? 'bg-navy-800 shadow-lg' : 'bg-white shadow-md'} transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:hidden`} data-id="element-1669">
          <div className="pt-20" data-id="element-1670">
            <TeacherSidebar isExpanded={true} isPinned={true} onPinToggle={() => {}} onArrowToggle={() => {}} closeSidebar={() => setSidebarOpen(false)} data-id="element-1671" />
          </div>
        </div>
        {/* Main content - shifts with sidebar */}
        <div style={{
        paddingLeft: undefined
      }} className={`w-full pt-20 transition-[padding-left] duration-200 ease-in-out ${isPinned ? 'lg:pl-64' : 'lg:pl-[72px]'}`} data-id="element-1672">
          <main className={`${isMessagesPage ? 'p-0' : 'p-6'} ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1673">
            {children}
          </main>
        </div>
      </div>
      {!isMessagesPage && <Footer data-id="element-1674" />}
    </div>;
};